import React from 'react';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { RequestButton, RequestButtonProps } from '@common/react/components/UI/LoadingButton/LoadingButton';
import { WithId } from '@common/typescript/objects/WithId';
import { ModalProps } from '@common/react/components/Modal/ModalContextProvider';
import { getDefaultOptions } from '@common/react/components/UI/Transmutation/defaultOptions';

interface TransmutationProps<
	TResponse extends WithId = WithId,
	TRequest extends WithId = TResponse,
	TModalProps = ModalProps,
> extends Omit<RequestButtonProps<TResponse, TRequest, TModalProps>, 'requestType' | 'requestProps' | 'children'> {
	requestProps: TRequest;
	requestType?: string;
	children?: React.ReactNode;
	disabled?: boolean;
}

const Transmutation: <TResponse extends WithId = WithId, TRequest extends WithId = TResponse, TModalProps = ModalProps>(
	props: TransmutationProps<TResponse, TRequest, TModalProps>,
) => React.ReactElement = <TResponse extends WithId = WithId, TRequest extends WithId = TResponse, TModalProps = ModalProps>(
	props: TransmutationProps<TResponse, TRequest, TModalProps>,
) => {
	const { onTransmutation, getUser } = useApplicationContext();
	const options = getDefaultOptions<TRequest>(props.requestProps.id, onTransmutation);

	const {
		requestProps,
		requestType = options.requestType,
		className = 'btn btn-sm btn-default',
		disabled = false,
		title = 'Login as',
		onSuccess = options.onSuccess,
		onError = options.onError,
		children = <i className="fa fa-child" aria-hidden="true" />,
		...rest
	} = props;

	return <RequestButton<TResponse, TRequest, TModalProps>
		requestType={requestType}
		requestProps={requestProps}
		onSuccess={onSuccess}
		onError={onError}
		className={className}
		title={title}
		disabled={requestProps.id === getUser()?.id || disabled}
		{...rest}
	>
		{children}
	</RequestButton>;
};

export default Transmutation;
